<template>
  <Teleport to="body">
    <section
      v-if="actions.length"
      class="virtual-print-desk"
      aria-label="Impresiones virtuales pendientes"
    >
      <article
        v-for="action in visibleActions"
        :key="action.action_id"
        class="virtual-print-window"
        :class="{
          'is-dragging': draggingActionId === action.action_id,
          'has-preview': isPreviewOpen(action.action_id),
        }"
        :style="windowStyle(action)"
        @pointerdown="focusWindow(action.action_id)"
      >
        <header
          class="virtual-print-window__bar"
          @pointerdown="startDrag($event, action.action_id)"
        >
          <div class="virtual-print-window__status" aria-hidden="true"></div>
          <span>Impresora virtual</span>
          <div class="virtual-print-window__controls">
            <button
              type="button"
              class="virtual-print-window__control"
              :aria-label="`Minimizar ventana de ${action.printer_name}`"
              @pointerdown.stop
              @click.stop="minimizeAction(action.action_id)"
            >
              −
            </button>
            <button
              type="button"
              class="virtual-print-window__control"
              :disabled="closingIds.has(action.action_id)"
              :aria-label="`Cerrar ventana de ${action.printer_name}`"
              @pointerdown.stop
              @click.stop="closeAction(action)"
            >
              ×
            </button>
          </div>
        </header>

        <div class="virtual-print-window__body">
          <span class="virtual-print-window__eyebrow">Flujo activado</span>
          <h2>{{ action.printer_name }}</h2>
          <p>
            EWU ha llamado a esta impresora virtual para
            <strong>{{ documentLabel(action) }}</strong>.
          </p>

          <div class="virtual-print-window__preview-row">
            <button
              type="button"
              class="virtual-print-window__preview-toggle"
              @pointerdown.stop
              @click.stop="togglePreview(action)"
            >
              {{ isPreviewOpen(action.action_id) ? 'Ocultar documento' : 'Ver documento' }}
            </button>
            <span
              v-if="previewMessages[action.action_id]"
              class="virtual-print-window__preview-message"
              :class="{ 'is-error': previewErrorState[action.action_id] }"
            >
              {{ previewMessages[action.action_id] }}
            </span>
          </div>

          <iframe
            v-if="isPreviewOpen(action.action_id) && hasPreviewContent(action)"
            class="virtual-print-window__preview"
            :srcdoc="action.html_content"
            sandbox=""
            :title="`Documento para ${action.printer_name}`"
            @load="handlePreviewLoaded(action.action_id)"
          ></iframe>
        </div>

        <div class="virtual-print-window__footer" role="group" aria-label="Acciones de impresión virtual">
          <span>{{ action.virtual_kind ? action.virtual_kind.toUpperCase() : 'VIRTUAL' }}</span>
          <button
            type="button"
            :disabled="closingIds.has(action.action_id)"
            @pointerdown.stop
            @click.stop="closeAction(action)"
          >
            {{ closingIds.has(action.action_id) ? 'Cerrando…' : 'Entendido' }}
          </button>
        </div>
      </article>

      <nav v-if="minimizedActions.length" class="virtual-print-taskbar" aria-label="Ventanas minimizadas">
        <button
          v-for="action in minimizedActions"
          :key="`minimized-${action.action_id}`"
          type="button"
          @click="restoreAction(action.action_id)"
        >
          <span>{{ action.virtual_kind ? action.virtual_kind.toUpperCase() : 'VIRTUAL' }}</span>
          {{ action.printer_name }}
        </button>
      </nav>
    </section>
  </Teleport>
</template>

<script>
import {
  acknowledgeVirtualPrintAction,
  getPendingVirtualPrintActions,
  VIRTUAL_PRINT_ACTIONS_EVENT,
} from '@/services/printing/printAgentService';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_INTERVAL_MS = 60000;
const WINDOW_WIDTH = 350;
const PREVIEW_WINDOW_WIDTH = 700;
const WINDOW_HEIGHT = 300;
const PREVIEW_WINDOW_HEIGHT = 620;
const WINDOW_GAP = 22;

export default {
  name: 'VirtualPrintActionManager',
  props: {
    enabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      actions: [],
      previewOpenState: {},
      previewErrorState: {},
      previewMessages: {},
      previewTimers: {},
      closingIds: new Set(),
      dismissedIds: new Set(),
      windowStates: {},
      draggingActionId: null,
      dragOffset: { x: 0, y: 0 },
      topZIndex: 21010,
      pollTimer: null,
      pollInFlight: false,
      printScopeKey: '',
      pollFailures: 0,
    };
  },
  watch: {
    enabled: {
      immediate: true,
      handler(enabled) {
        if (enabled) {
          this.startPolling();
        } else {
          this.stopPolling();
          this.actions = [];
        }
      },
    },
  },
  beforeUnmount() {
    this.stopPolling();
    this.stopDrag();
    Object.values(this.previewTimers).forEach((timer) => window.clearTimeout(timer));
  },
  computed: {
    visibleActions() {
      return this.actions.filter((action) => !this.windowStates[action.action_id]?.minimized);
    },
    minimizedActions() {
      return this.actions.filter((action) => this.windowStates[action.action_id]?.minimized);
    },
  },
  methods: {
    startPolling() {
      if (this.pollTimer || typeof window === 'undefined') return;
      window.addEventListener(VIRTUAL_PRINT_ACTIONS_EVENT, this.handleImmediateActions);
      window.addEventListener('resize', this.handleViewportResize);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      void this.poll().finally(() => this.schedulePoll());
    },
    stopPolling() {
      if (this.pollTimer) {
        window.clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener(VIRTUAL_PRINT_ACTIONS_EVENT, this.handleImmediateActions);
        window.removeEventListener('resize', this.handleViewportResize);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      }
    },
    schedulePoll() {
      if (!this.enabled || this.pollTimer) return;
      const delay = document.visibilityState === 'hidden'
        ? MAX_POLL_INTERVAL_MS
        : Math.min(
          MAX_POLL_INTERVAL_MS,
          POLL_INTERVAL_MS * (2 ** Math.min(this.pollFailures, 4)),
        );
      this.pollTimer = window.setTimeout(async () => {
        this.pollTimer = null;
        await this.poll();
        this.schedulePoll();
      }, delay);
    },
    handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      if (this.pollTimer) {
        window.clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      void this.poll().finally(() => this.schedulePoll());
    },
    async poll() {
      if (!this.enabled || this.pollInFlight || document.visibilityState === 'hidden') return;
      this.pollInFlight = true;
      try {
        const response = await getPendingVirtualPrintActions();
        if (!response.ok) {
          this.pollFailures += 1;
          return;
        }

        this.pollFailures = 0;
        if (this.printScopeKey !== response.scope_key) {
          this.actions = [];
          this.windowStates = {};
          this.printScopeKey = response.scope_key;
        }
        this.mergeActions(response.actions);
      } finally {
        this.pollInFlight = false;
      }
    },
    handleImmediateActions(event) {
      this.mergeActions(Array.isArray(event?.detail) ? event.detail : []);
    },
    mergeActions(actions = []) {
      const current = new Map(this.actions.map((action) => [action.action_id, action]));
      actions.forEach((action) => {
        if (!action?.action_id || this.dismissedIds.has(action.action_id)) return;
        if (!this.windowStates[action.action_id]) {
          this.initializeWindow(action.action_id, current.size);
        }
        current.set(action.action_id, action);
      });
      this.actions = [...current.values()].sort((left, right) => (
        String(left.created_at || '').localeCompare(String(right.created_at || ''))
      ));
    },
    initializeWindow(actionId, index = 0) {
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
      const column = index % 3;
      const row = Math.floor(index / 3) % 3;
      const x = Math.max(12, viewportWidth - WINDOW_WIDTH - 24 - (column * WINDOW_GAP));
      const y = Math.max(12, viewportHeight - WINDOW_HEIGHT - 24 - (row * WINDOW_GAP));
      this.topZIndex += 1;
      this.windowStates = {
        ...this.windowStates,
        [actionId]: {
          x,
          y,
          zIndex: this.topZIndex,
          minimized: false,
        },
      };
    },
    windowStyle(action) {
      const state = this.windowStates[action.action_id] || {};
      return {
        left: `${state.x || 12}px`,
        top: `${state.y || 12}px`,
        zIndex: state.zIndex || 21010,
      };
    },
    focusWindow(actionId) {
      const current = this.windowStates[actionId];
      if (!current) return;
      this.topZIndex += 1;
      this.windowStates = {
        ...this.windowStates,
        [actionId]: {
          ...current,
          zIndex: this.topZIndex,
        },
      };
    },
    startDrag(event, actionId) {
      if (event.button !== 0 || window.matchMedia('(max-width: 760px)').matches) return;
      const state = this.windowStates[actionId];
      if (!state) return;
      this.focusWindow(actionId);
      this.draggingActionId = actionId;
      this.dragOffset = {
        x: event.clientX - state.x,
        y: event.clientY - state.y,
      };
      window.addEventListener('pointermove', this.handleDrag);
      window.addEventListener('pointerup', this.stopDrag, { once: true });
      event.preventDefault();
    },
    handleDrag(event) {
      const actionId = this.draggingActionId;
      const state = actionId ? this.windowStates[actionId] : null;
      if (!state) return;
      const windowWidth = this.isPreviewOpen(actionId) ? PREVIEW_WINDOW_WIDTH : WINDOW_WIDTH;
      const windowHeight = this.isPreviewOpen(actionId) ? PREVIEW_WINDOW_HEIGHT : WINDOW_HEIGHT;
      const maxX = Math.max(12, window.innerWidth - Math.min(windowWidth, window.innerWidth - 24) - 12);
      const maxY = Math.max(12, window.innerHeight - Math.min(windowHeight, window.innerHeight - 24) - 12);
      this.windowStates = {
        ...this.windowStates,
        [actionId]: {
          ...state,
          x: Math.min(maxX, Math.max(12, event.clientX - this.dragOffset.x)),
          y: Math.min(maxY, Math.max(12, event.clientY - this.dragOffset.y)),
        },
      };
    },
    stopDrag() {
      this.draggingActionId = null;
      if (typeof window !== 'undefined') {
        window.removeEventListener('pointermove', this.handleDrag);
      }
    },
    minimizeAction(actionId) {
      const state = this.windowStates[actionId];
      if (!state) return;
      this.windowStates = {
        ...this.windowStates,
        [actionId]: { ...state, minimized: true },
      };
    },
    restoreAction(actionId) {
      const state = this.windowStates[actionId];
      if (!state) return;
      this.topZIndex += 1;
      this.windowStates = {
        ...this.windowStates,
        [actionId]: {
          ...state,
          minimized: false,
          zIndex: this.topZIndex,
        },
      };
    },
    documentLabel(action) {
      const eventKey = action?.metadata?.print_event || '';
      const labels = {
        partner_ws_order_created: 'una comanda QR',
        pos_order_created: 'una comanda TPV',
        manual_kitchen_print: 'una comanda manual',
        account_print: 'una cuenta',
        invoice_simplified_print: 'un ticket simplificado',
        invoice_complete_print: 'una factura completa',
      };
      return labels[eventKey] || 'un documento';
    },
    hasPreviewContent(action) {
      return Boolean(String(action?.html_content || '').trim());
    },
    isPreviewOpen(actionId) {
      return this.previewOpenState[actionId] === true;
    },
    setPreviewMessage(actionId, message, isError = false) {
      this.previewMessages = {
        ...this.previewMessages,
        [actionId]: message,
      };
      this.previewErrorState = {
        ...this.previewErrorState,
        [actionId]: isError,
      };
    },
    togglePreview(action) {
      const actionId = action?.action_id;
      if (!actionId) return;
      const shouldOpen = !this.isPreviewOpen(actionId);
      this.previewOpenState = {
        ...this.previewOpenState,
        [actionId]: shouldOpen,
      };
      this.constrainWindowToViewport(actionId, shouldOpen);
      if (!shouldOpen) {
        this.setPreviewMessage(actionId, '');
      } else {
        if (!this.hasPreviewContent(action)) {
          this.setPreviewMessage(actionId, 'Documento no disponible', true);
        } else {
          this.setPreviewMessage(actionId, 'Cargando vista previa…');
          window.clearTimeout(this.previewTimers[actionId]);
          this.previewTimers[actionId] = window.setTimeout(() => {
            if (this.isPreviewOpen(actionId)) {
              this.setPreviewMessage(actionId, 'No se pudo confirmar la carga', true);
            }
          }, 2500);
        }
      }
    },
    constrainWindowToViewport(actionId, previewOpen = this.isPreviewOpen(actionId)) {
      const state = this.windowStates[actionId];
      if (!state || typeof window === 'undefined') return;
      const desiredWidth = previewOpen ? PREVIEW_WINDOW_WIDTH : WINDOW_WIDTH;
      const desiredHeight = previewOpen ? PREVIEW_WINDOW_HEIGHT : WINDOW_HEIGHT;
      const actualWidth = Math.min(desiredWidth, window.innerWidth - 24);
      const actualHeight = Math.min(desiredHeight, window.innerHeight - 24);
      const maxX = Math.max(12, window.innerWidth - actualWidth - 12);
      const maxY = Math.max(12, window.innerHeight - actualHeight - 12);
      this.windowStates = {
        ...this.windowStates,
        [actionId]: {
          ...state,
          x: Math.min(maxX, Math.max(12, state.x)),
          y: Math.min(maxY, Math.max(12, state.y)),
        },
      };
    },
    handleViewportResize() {
      this.actions.forEach((action) => {
        this.constrainWindowToViewport(action.action_id);
      });
    },
    handlePreviewLoaded(actionId) {
      window.clearTimeout(this.previewTimers[actionId]);
      this.setPreviewMessage(actionId, 'Vista previa cargada');
    },
    async closeAction(action) {
      if (!action?.action_id || this.closingIds.has(action.action_id)) return;

      this.closingIds = new Set([...this.closingIds, action.action_id]);
      try {
        const response = await acknowledgeVirtualPrintAction(action.action_id);
        if (!response?.ok) return;
        this.dismissedIds = new Set([...this.dismissedIds, action.action_id]);
        this.actions = this.actions.filter((item) => item.action_id !== action.action_id);
        const nextWindowStates = { ...this.windowStates };
        delete nextWindowStates[action.action_id];
        this.windowStates = nextWindowStates;
        const nextPreviewState = { ...this.previewOpenState };
        delete nextPreviewState[action.action_id];
        this.previewOpenState = nextPreviewState;
        const nextErrorState = { ...this.previewErrorState };
        delete nextErrorState[action.action_id];
        this.previewErrorState = nextErrorState;
        window.clearTimeout(this.previewTimers[action.action_id]);
        const nextMessages = { ...this.previewMessages };
        delete nextMessages[action.action_id];
        this.previewMessages = nextMessages;
      } finally {
        const nextClosingIds = new Set(this.closingIds);
        nextClosingIds.delete(action.action_id);
        this.closingIds = nextClosingIds;
      }
    },
  },
};
</script>

<style scoped>
.virtual-print-desk {
  position: fixed;
  inset: 0;
  z-index: 21000;
  pointer-events: none;
}

.virtual-print-window {
  position: absolute;
  display: flex;
  flex-direction: column;
  width: min(350px, calc(100vw - 32px));
  max-height: min(620px, calc(100vh - 40px));
  min-height: 0;
  overflow: hidden;
  background: #f7f4ec;
  color: #18201d;
  border: 1px solid #1d2925;
  border-radius: 14px;
  box-shadow: 8px 10px 0 rgba(24, 32, 29, 0.18);
  pointer-events: auto;
  animation: virtual-window-in 240ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.virtual-print-window.has-preview {
  width: min(700px, calc(100vw - 32px));
  height: min(620px, calc(100vh - 40px));
}

.virtual-print-window.is-dragging {
  user-select: none;
  box-shadow: 12px 16px 0 rgba(24, 32, 29, 0.14);
}

.virtual-print-window__bar {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 9px;
  min-height: 38px;
  padding: 0 9px 0 12px;
  color: #fff;
  background: #18201d;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: grab;
  touch-action: none;
}

.virtual-print-window.is-dragging .virtual-print-window__bar {
  cursor: grabbing;
}

.virtual-print-window__status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f2c14e;
  box-shadow: 0 0 0 3px rgba(242, 193, 78, 0.18);
}

.virtual-print-window__controls {
  display: flex;
  gap: 3px;
}

.virtual-print-window__control {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.virtual-print-window__control:hover {
  background: rgba(255, 255, 255, 0.12);
}

.virtual-print-taskbar {
  position: fixed;
  right: 18px;
  bottom: 16px;
  left: 18px;
  z-index: 21999;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  pointer-events: auto;
}

.virtual-print-taskbar button {
  max-width: 260px;
  overflow: hidden;
  border: 1px solid #18201d;
  border-radius: 9px;
  padding: 8px 12px;
  color: #fff;
  background: #18201d;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 800;
}

.virtual-print-taskbar span {
  margin-right: 7px;
  color: #f2c14e;
  font-size: 9px;
  letter-spacing: 0.08em;
}

.virtual-print-window__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 20px 20px 16px;
  overscroll-behavior: contain;
}

.virtual-print-window__eyebrow {
  display: block;
  margin-bottom: 5px;
  color: #8a5b16;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.virtual-print-window h2 {
  margin: 0;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 24px;
  line-height: 1.08;
}

.virtual-print-window p {
  margin: 12px 0 16px;
  color: #53605b;
  font-size: 13px;
  line-height: 1.45;
}

.virtual-print-window__preview-toggle {
  border: 1px solid #18201d;
  border-radius: 7px;
  padding: 6px 10px;
  color: #fff;
  background: #18201d;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  pointer-events: auto;
}

.virtual-print-window__preview-toggle:hover {
  background: #33413c;
}

.virtual-print-window__preview-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.virtual-print-window__preview-message {
  color: #43705f;
  font-size: 10px;
  font-weight: 800;
}

.virtual-print-window__preview-message.is-error {
  color: #a33b2b;
}

.virtual-print-window__preview {
  display: block;
  width: 100%;
  height: min(360px, calc(100vh - 260px));
  min-height: 220px;
  margin-top: 14px;
  border: 1px solid #cbc5b7;
  border-radius: 8px;
  background: #fff;
}

.virtual-print-window__footer {
  position: static;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 12px 11px 20px;
  border-top: 1px solid #d9d3c6;
  background: #f7f4ec;
}

.virtual-print-window__footer span {
  color: #75807b;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.12em;
}

.virtual-print-window__footer button {
  border: 1px solid #18201d;
  border-radius: 8px;
  padding: 7px 13px;
  color: #fff;
  background: #18201d;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.virtual-print-window button:disabled {
  cursor: wait;
  opacity: 0.55;
}

@keyframes virtual-window-in {
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 760px) {
  .virtual-print-desk {
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-start;
    gap: 10px;
    padding: 12px;
    overflow-y: auto;
  }

  .virtual-print-window {
    position: relative;
    top: auto !important;
    left: auto !important;
    width: 100%;
    flex: 0 0 auto;
    max-height: calc(100dvh - 24px);
    box-shadow: 5px 6px 0 rgba(24, 32, 29, 0.18);
  }

  .virtual-print-window.has-preview {
    height: min(620px, calc(100dvh - 24px));
  }

  .virtual-print-window__bar {
    cursor: default;
  }

  .virtual-print-taskbar {
    position: sticky;
    right: auto;
    bottom: 0;
    left: auto;
    flex-wrap: wrap;
  }
}
</style>
